/*************************************************/
/* Slider                                        */
/*************************************************/
import Swiper from "swiper";
import { Controller, EffectCreative, Navigation, Pagination } from "swiper/modules";
import customEventsNames from './../customEventsNames';

export function slider(headerController) {
    const cardsSelector = '.cards__swiper';
    const cardsListSelector = '.cards__list';
    const cardSelector = '.card';
    const contentBoxesSelector = '.content-boxes__swiper';
    const counterSelector = '.counter-component__swiper';
    const counterListSelector = '.counter-component__items';
    const counterItemSelector = '.counter-component__item';
    const articlesSelector = '.articles-list__swiper';
    const articlesListSelector = '.articles-list__items';
    const articlesItemSelector = '.articles-list__item';
    const quotesSelector = '.quote-component__inner.swiper';
    const scrollSectionSelector = '.scroll-section-component__swiper.swiper';
    const scrollSectionInnerSelector = '.scroll-section-component__inner';
    const scrollSectionPaginationSelector = '.scroll-section-component__pagination';
    const scrollSectionMeasuringClass = 'scroll-section-component__swiper--measuring';
    const scrollSectionMobileBreakpoint = 768;
    const bulletActiveClass = 'swiper-pagination-bullet-active';
    const applicationImageSelector = '.application-image';
    const applicationImageSliderClass = 'application-image--slider';
    const applicationImageMediaContainerSelector = '.application-image__media-container';
    const applicationImageMediaListSelector = '.application-image__media-list';
    const applicationImageMediaSelector = '.application-image__media';
    const applicationImageTextPanelsSelector = '.application-image__text-panels';
    const applicationImageTextPanelListSelector = '.application-image__text-panel-list';
    const applicationImageTextPanelSelector = '.accordion__text-panel';
    const applicationImagePrevSelector = '.application-image__nav--prev';
    const applicationImageNextSelector = '.application-image__nav--next';

    const mobileBreakpoint = 992;
    const mq = window.matchMedia(`(max-width: ${mobileBreakpoint - 1}px)`);
    const scrollSectionMq = window.matchMedia(`(max-width: ${scrollSectionMobileBreakpoint}px)`);
    const instances = new Map();

    // Must be <main>, not <body>: the header carries its own mmenu transform,
    // making <body> its containing block for position:fixed — freezing <body>
    // would drag the header out of view with it.
    const scrollLockElement = document.getElementById('mainContent') || document.body;

    const mobileCarouselConfig = {
        speed: 500,
        slidesPerView: 1.2,
        spaceBetween: 15,
    };

    /*************************************************/
    /* Scroll lock                                   */
    /*************************************************/

    // Finger travel, in px, that advances one slide while locked
    const swipeStep = 60;

    // Pinning the page is a desktop behaviour. Below the mobile breakpoint the
    // page scrolls as it normally would and the sliders are swiped directly.
    const scrollLockEnabled = () => !mq.matches;

    // Sections that pin the page, keyed by the element whose centre engages the
    // pin. Only one can hold the page at a time, so the lock state and the input
    // listeners are shared — held per section, the scroll that one section's
    // engage or release provokes reads as a real gesture in the others.
    const lockedSections = new Map();
    let lockOwner = null;
    let pendingRelease = false;
    let savedScrollY = 0;
    let lastScrollY = window.scrollY;
    let lastTouchY = 0;
    let lastTouchDirection = 0;
    let swipeTravel = 0;
    let touchInput = false;
    let touchActive = false;

    const preventScroll = (e) => {
        if (e?.cancelable) e.preventDefault();
    };

    const viewportHeight = () => window.visualViewport?.height ?? window.innerHeight;

    // Closest to the viewport centre wins rather than first in DOM order:
    // scrolling up makes every section below the centre eligible, so first-match
    // could pin one barely poking into the viewport.
    const findLockCandidate = (deltaY) => {
        let candidate = null;
        let shortestDistance = Infinity;
        const viewport = viewportHeight();

        lockedSections.forEach(section => {
            const { pinTarget, swiper } = section;

            // Nothing left to advance through this way, so let the page scroll
            // past. Tested before the rect to keep the layout read off this path.
            if (deltaY > 0 ? swiper.isEnd : swiper.isBeginning) return;

            const rect = pinTarget.getBoundingClientRect();

            // A section scrolled out of view still satisfies the offset test below,
            // so it would fight the one that is actually on screen.
            if (rect.bottom < 0 || rect.top > viewport) return;

            const room = viewport - rect.height;
            const engageTop = deltaY > 0 ? Math.max(room / 2, 0) : Math.min(room / 2, room);
            const offset = rect.top - engageTop;
            if (deltaY > 0 ? offset > 0 : offset < 0) return;

            if (Math.abs(offset) < shortestDistance) {
                shortestDistance = Math.abs(offset);
                candidate = section;
            }
        });

        return candidate;
    };

    const engageLock = (section) => {
        savedScrollY = window.scrollY;
        scrollLockElement.style.position = 'fixed';
        scrollLockElement.style.top = `-${savedScrollY}px`;
        scrollLockElement.style.width = '100%';
        headerController?.pause();
        document.body.classList.add('header-sticky');
        lockOwner = section;
        pendingRelease = false;

        // Freezing the element collapses the document, so the browser scrolls of
        // its own accord — re-baseline so onScroll reads no delta from it.
        lastScrollY = window.scrollY;
    };

    const releaseLock = () => {
        scrollLockElement.style.position = '';
        scrollLockElement.style.top = '';
        scrollLockElement.style.width = '';

        // scrollTo clamps to the height in effect when it lands, so flush layout
        // first or the restore clamps against the collapsed height and hits the top
        void document.documentElement.scrollHeight;
        window.scrollTo(0, savedScrollY);

        headerController?.resume(savedScrollY);
        lockOwner = null;
        pendingRelease = false;

        // Baseline against the position asked for, not one read back: the scroll
        // may not have landed yet, and a stale baseline reads as a gesture.
        lastScrollY = savedScrollY;
    };

    // Shared by wheel, touch and scroll. `commit` says whether the delta should
    // change slide: a wheel tick always does, finger travel only once it adds up
    // to swipeStep, and a scroll never does — it only exists to engage the lock.
    const handleGesture = (deltaY, e, commit) => {
        // Boundary reached, waiting for the finger to lift; hold the page still
        if (pendingRelease) {
            // Unless the finger has already gone and its touchend never arrived —
            // waiting on a lift that isn't coming pins the page for good.
            if (!touchActive) {
                pendingRelease = false;
                releaseLock();
                return;
            }

            preventScroll(e);
            return;
        }

        const section = lockOwner ?? findLockCandidate(deltaY);
        if (!section) return;
        if (!lockOwner) engageLock(section);

        const { swiper, isTransitioning } = section;

        // isBeginning/isEnd flip as soon as a slide change starts, so wait for the
        // transition to finish before treating it as the end of the stack.
        const atStart = swiper.isBeginning && !isTransitioning && deltaY < 0;
        const atEnd = swiper.isEnd && !isTransitioning && deltaY > 0;

        if (atStart || atEnd) {
            // A pan underway captured scroll 0 as its origin while the document
            // was collapsed, so releasing mid-swipe lets the browser carry on
            // panning from 0 and overshoot to the top. Wait for the finger.
            if (touchActive) {
                pendingRelease = true;
                preventScroll(e);
                return;
            }

            releaseLock();
            return;
        }

        preventScroll(e);
        if (isTransitioning || !commit) return;
        deltaY > 0 ? swiper.slideNext() : swiper.slidePrev();
    };

    // A wheel that coasts — a Magic Mouse, or any trackpad — reports one flick as a
    // long stream of events, and it is the momentum tail, running on for seconds
    // after the reader's hand has stopped, that turns one flick into three or four
    // slides. The tail is recognisable: its deltas decay. So a commit also needs
    // the recent stream to be holding steady or growing, which it is while the
    // reader drives it and isn't once it is only coasting. Comparing a window
    // rather than single events matters — a slow decay repeats values as it rounds
    // (5, 5, 4, 4) and each one on its own still looks steady. A notched wheel
    // repeats the same delta in earnest, so it stays on the driven side of this and
    // is paced by the slide transition, as before.
    const wheelHistoryMs = 150;

    // What counts as the dribble a flick ends on, measured against the peak of the
    // burst it belongs to rather than a fixed number of pixels: scrolling slowly
    // and deliberately produces deltas every bit as small as a spent flick does,
    // and a fixed floor holds the page still while refusing to move the slides.
    const wheelTailFraction = 0.1;

    let wheelHistory = [];
    let wheelBurstPeak = 0;
    let lastWheelSign = 0;

    const wheelIsDriven = (absDelta, now) => {
        // A gap longer than the window ends the burst, which is what makes the
        // next flick — or a slow scroll after one — a fresh gesture.
        const last = wheelHistory[wheelHistory.length - 1];
        if (last && now - last.t > wheelHistoryMs) wheelBurstPeak = 0;

        wheelHistory = wheelHistory.filter(sample => now - sample.t <= wheelHistoryMs);
        wheelHistory.push({ t: now, v: absDelta });
        wheelBurstPeak = Math.max(wheelBurstPeak, absDelta);

        if (absDelta < wheelBurstPeak * wheelTailFraction) return false;

        // Too little history to tell a flick from its tail — and the start of a
        // gesture is exactly where the reader expects it to move.
        if (wheelHistory.length < 4) return true;

        const half = Math.floor(wheelHistory.length / 2);
        const mean = samples => samples.reduce((total, s) => total + s.v, 0) / samples.length;

        return mean(wheelHistory.slice(half)) >= mean(wheelHistory.slice(0, half));
    };

    // Firefox reports lines, and full pages when a wheel is mapped that way, so the
    // magnitudes above only mean anything once they're in pixels.
    const wheelPixels = (e) => {
        if (e.deltaMode === 1) return e.deltaY * 16;
        if (e.deltaMode === 2) return e.deltaY * viewportHeight();

        return e.deltaY;
    };

    const onWheel = (e) => {
        touchInput = false;

        const deltaY = wheelPixels(e);
        const sign = Math.sign(deltaY);

        // Turning back is a new gesture, not the tail of the last one
        if (sign !== lastWheelSign) {
            wheelHistory = [];
            wheelBurstPeak = 0;
        }

        lastWheelSign = sign;

        const commit = wheelIsDriven(Math.abs(deltaY), performance.now());

        handleGesture(deltaY, e, commit);
    };

    const onTouchStart = (e) => {
        touchInput = true;
        touchActive = true;
        if (e.touches.length !== 1) return;

        lastTouchY = e.touches[0].clientY;
        swipeTravel = 0;
    };

    const onTouchMove = (e) => {
        if (e.touches.length !== 1) return;

        const currentY = e.touches[0].clientY;

        // Sign-matched to wheel: dragging the content up is "scrolling down"
        const deltaY = lastTouchY - currentY;
        lastTouchY = currentY;
        if (deltaY === 0) return;

        // Reversing direction restarts the count, so a swipe back doesn't first
        // have to undo the travel banked the other way.
        if (deltaY > 0 !== swipeTravel > 0) swipeTravel = 0;
        swipeTravel += deltaY;
        lastTouchDirection = deltaY;

        const commit = Math.abs(swipeTravel) >= swipeStep;
        if (commit) swipeTravel = 0;

        handleGesture(deltaY, e, commit);
    };

    const onTouchEnd = () => {
        touchActive = false;
        swipeTravel = 0;

        if (!pendingRelease) return;

        pendingRelease = false;
        releaseLock();
    };

    // A flick keeps scrolling after the finger lifts, with no touch events left to
    // notice the section reaching the centre — so engage from scroll as well.
    // Touch input only, to leave the wheel path untouched.
    const onScroll = () => {
        const deltaY = window.scrollY - lastScrollY;
        lastScrollY = window.scrollY;
        if (lockOwner || !touchInput || deltaY === 0) return;

        // Momentum always carries on the way the finger went, so a delta against
        // it is the page settling after our own release moved it, not input.
        if (deltaY > 0 !== lastTouchDirection > 0) return;

        handleGesture(deltaY, null, false);
    };

    let inputListenersAttached = false;

    // Non-passive wheel and touchmove make the browser wait on JS before it can
    // scroll, so they go on only for a page that has a section to pin, and only
    // at a width that pins it. They no-op until handleGesture finds a section
    // that wants the lock.
    const attachInputListeners = () => {
        if (inputListenersAttached || !lockedSections.size) return;

        inputListenersAttached = true;
        window.addEventListener('wheel', onWheel, { passive: false });
        window.addEventListener('touchstart', onTouchStart, { passive: true });
        window.addEventListener('touchmove', onTouchMove, { passive: false });
        window.addEventListener('touchend', onTouchEnd, { passive: true });
        window.addEventListener('touchcancel', onTouchEnd, { passive: true });
        window.addEventListener('scroll', onScroll, { passive: true });
    };

    const detachInputListeners = () => {
        if (!inputListenersAttached) return;

        inputListenersAttached = false;
        window.removeEventListener('wheel', onWheel);
        window.removeEventListener('touchstart', onTouchStart);
        window.removeEventListener('touchmove', onTouchMove);
        window.removeEventListener('touchend', onTouchEnd);
        window.removeEventListener('touchcancel', onTouchEnd);
        window.removeEventListener('scroll', onScroll);
    };

    // A pinned section is driven by the page gesture, so its own touch handling
    // would fight it. Unpinned, the finger drives it directly instead.
    const syncSectionTouch = (section) => {
        const allow = !scrollLockEnabled();

        section.swiper.params.allowTouchMove = allow;
        section.swiper.allowTouchMove = allow;
        section.swiper.params.simulateTouch = allow;
    };

    // Runs at the end of every init, so a window crossing the breakpoint hands
    // the page back before the listeners that were holding it go.
    const syncScrollLock = () => {
        if (scrollLockEnabled()) {
            attachInputListeners();
        } else {
            if (lockOwner) releaseLock();

            pendingRelease = false;
            touchActive = false;
            swipeTravel = 0;
            detachInputListeners();
        }

        lockedSections.forEach(syncSectionTouch);
    };

    const createScrollLockedSlides = (pinTarget, swiper, slideCount) => {
        if (slideCount <= 1 || lockedSections.has(pinTarget)) return;

        const section = { pinTarget, swiper, isTransitioning: false, transitionTimer: null };

        syncSectionTouch(section);

        // Both the slide change and the release out of the section wait on this
        // flag, so a transition whose end never arrives holds the page for good —
        // and the page has nothing left to scroll while it does. Swiper's end
        // event can go missing for reasons outside this module: an update() that
        // lands mid-transition (a resize does that, and engaging the lock provokes
        // one by taking the scrollbar away), or a tab that stops painting. So the
        // flag clears on its own once the transition is past due, whatever Swiper
        // does or doesn't say.
        const settle = () => {
            clearTimeout(section.transitionTimer);
            section.isTransitioning = false;
        };

        swiper.on('slideChangeTransitionStart', () => {
            section.isTransitioning = true;
            clearTimeout(section.transitionTimer);
            section.transitionTimer = setTimeout(settle, swiper.params.speed + 100);
        });

        swiper.on('slideChangeTransitionEnd', settle);

        lockedSections.set(pinTarget, section);
    };

    /*************************************************/
    /* Sliders                                       */
    /*************************************************/

    const handleSlideChangeTransitionEnd = (swiper) => {
        swiper.el.querySelectorAll('.swiper-slide > a').forEach(slide => {
            slide.setAttribute('tabindex', '-1');
        });

        const activeSlide = swiper.el.querySelector('.swiper-slide-active > a');
        if (activeSlide != null) {
            activeSlide.setAttribute('tabindex', '0');
        }
    };

    const handleSlideChange = () => {
        const nextDuplicateSlide = document.querySelector('.swiper-slide-duplicate.swiper-slide-next');
        if (nextDuplicateSlide == null) return;

        const unloadedSlideImage = nextDuplicateSlide.querySelector('img.lazyloading');
        if (unloadedSlideImage != null) {
            unloadedSlideImage.classList.replace('lazyloading', 'lazyloaded');
        }
    };

    const toggleSwiperClasses = (container, add, listSelector = cardsListSelector, slideSelector = cardSelector) => {
        container.classList.toggle('swiper', add);
        container.querySelector(listSelector).classList.toggle('swiper-wrapper', add);
        container.querySelectorAll(slideSelector).forEach(slide => slide.classList.toggle('swiper-slide', add));
    };

    // Cards swiper — mobile only
    const initCards = () => {
        document.querySelectorAll(cardsSelector).forEach(card => {
            if (mq.matches) {
                if (instances.has(card)) return;

                toggleSwiperClasses(card, true);
                instances.set(card, new Swiper(card, {
                    ...mobileCarouselConfig,
                    on: {
                        slideChange: handleSlideChange,
                        slideChangeTransitionEnd: handleSlideChangeTransitionEnd,
                    },
                }));
            } else if (instances.has(card)) {
                instances.get(card).destroy(true, true);
                instances.delete(card);
                toggleSwiperClasses(card, false);
            }
        });
    };

    // Counter component swiper — mobile only, the grid takes over above it
    const initCounters = () => {
        document.querySelectorAll(counterSelector).forEach(container => {
            if (container.querySelector(counterListSelector) == null) return;

            if (mq.matches) {
                if (instances.has(container)) return;

                toggleSwiperClasses(container, true, counterListSelector, counterItemSelector);
                instances.set(container, new Swiper(container, { ...mobileCarouselConfig }));
            } else if (instances.has(container)) {
                instances.get(container).destroy(true, true);
                instances.delete(container);
                toggleSwiperClasses(container, false, counterListSelector, counterItemSelector);
            }
        });
    };

    // Content boxes swiper — stacked cards, scroll-locked
    const contentBoxStackOffset = 0.25;
    const contentBoxScaleStep = 0.04;
    const contentBoxHeightProperty = '--content-box-height';

    // A card behind the front one is lifted by contentBoxStackOffset of its own
    // height and scaled down, and the scale gives back half the height it loses,
    // so this is how far up the stack reaches per step behind the front card.
    const contentBoxStackStep = contentBoxStackOffset - contentBoxScaleStep / 2;

    const initContentBoxes = () => {
        document.querySelectorAll(contentBoxesSelector).forEach(el => {
            if (instances.has(el)) return;

            const slides = [...el.querySelectorAll('.swiper-slide')];
            const slideCount = slides.length;

            // The swiper reserves the tallest card plus the lift of the whole
            // stack, so the component fits its own number of cards. Measured
            // with what was written last time cleared, or a remeasure builds
            // on its own output and the stack grows on every resize.
            const updateHeight = () => {
                el.style.removeProperty(contentBoxHeightProperty);
                el.style.height = 'auto';

                const cardHeight = Math.max(...slides.map(slide => slide.querySelector('.content-box').offsetHeight));

                el.style.setProperty(contentBoxHeightProperty, `${cardHeight}px`);
                el.style.height = `${cardHeight * (1 + contentBoxStackStep * (slideCount - 1))}px`;
            };

            const swiper = new Swiper(el, {
                modules: [EffectCreative],
                speed: 600,
                effect: 'creative',
                loop: false,
                creativeEffect: {
                    limitProgress: 10,
                    prev: { translate: [0, `${contentBoxStackOffset * -100}%`, -1], scale: 1 - contentBoxScaleStep, opacity: 1 },
                    next: { translate: [0, `${contentBoxStackOffset * -100}%`, -1], scale: 1 - contentBoxScaleStep, opacity: 1 },
                    perSlideOffset: 0,
                    perSlideRotate: 0,
                },
                grabCursor: false,
                on: {
                    afterInit: updateHeight,
                },
            });

            instances.set(el, swiper);

            // Mobile browsers fire resize as their toolbars collapse, and height
            // alone changing can't affect the stack — only remeasure on width.
            let lastWidth = window.innerWidth;
            window.addEventListener('resize', () => {
                if (window.innerWidth === lastWidth) return;

                lastWidth = window.innerWidth;
                updateHeight();
            });

            // Swiper stacks slides by their distance from the active one, but this
            // stack cycles: the card leaving the front drops to the back and the
            // rest move up one, so a slide's place in the stack is its rotation
            // around the active index. Every slide needs repositioning, not just
            // the one that left — and its z-index with it, or a slide sent to the
            // back stays layered over the ones now in front of it. The values
            // match Swiper's own creativeEffect maths, so slide one keeps the
            // layout it initialised with.
            const stackSlides = () => {
                swiper.slides.forEach((slideEl, i) => {
                    const position = (i - swiper.activeIndex + slideCount) % slideCount;

                    // Keep the X translation Swiper set for slide positioning
                    const xMatch = slideEl.style.transform.match(/translate3d\(([^,]+),/);
                    const xTranslate = xMatch ? xMatch[1].trim() : '0px';

                    slideEl.style.transform = `translate3d(${xTranslate}, ${position * contentBoxStackOffset * -100}%, ${-position}px) scale(${1 - position * contentBoxScaleStep})`;
                    slideEl.style.zIndex = slideCount - position;
                });
            };

            // Deferred so Swiper writes its own transforms first; ours then
            // animate to the corrected positions over the same transition.
            swiper.on('slideChangeTransitionStart', () => setTimeout(stackSlides, 0));

            // A finger makes Swiper rewrite every transform from its own distance
            // from the active slide, which is not the cyclic order. A drag that lands
            // on another slide is corrected above; one that doesn't — dragging on past
            // the last card — leaves Swiper's order standing, so it is corrected here.
            swiper.on('touchEnd', () => setTimeout(stackSlides, 0));

            createScrollLockedSlides(el.closest('.content-boxes__inner') || el, swiper, slideCount);
        });
    };

    // Application image — the media turns into a carousel on mobile, standing in
    // for the accordion. Swiping tells the accordion which item is active, so the
    // text panel and the title above the headline follow along.
    const activateApplicationImageItem = (component, index, includeMedia) => {
        const accordion = component.querySelector('.accordion');
        const item = accordion?.querySelectorAll('.accordion__item')[index];
        if (item == null) return;

        const activateEvent = new CustomEvent(customEventsNames.accordionActivateItemEventName, {
            detail: {
                accordionId: accordion.dataset.accordionId,
                accordionItemId: item.dataset.accordionItemId,
                accordionItemIndex: item.dataset.accordionItemIndex,
            },
        });

        accordion.dispatchEvent(activateEvent);
        component.querySelector(applicationImageTextPanelsSelector)?.dispatchEvent(activateEvent);

        // Only when the cross-fade is back in charge — while the slider owns the
        // media, its --active classes would fight the slide positions.
        if (includeMedia) {
            component.querySelector(applicationImageMediaContainerSelector)?.dispatchEvent(activateEvent);
        }
    };

    // The media and the text sit in different containers, so a single slide can't
    // hold both. Two swipers linked by the Controller module move as one instead,
    // tracking the finger together, and leave the desktop layout untouched.
    const applicationImageRegions = (component) => [
        {
            container: component.querySelector(applicationImageMediaContainerSelector),
            list: applicationImageMediaListSelector,
            slide: applicationImageMediaSelector,
        },
        {
            container: component.querySelector(applicationImageTextPanelsSelector),
            list: applicationImageTextPanelListSelector,
            slide: applicationImageTextPanelSelector,
        },
    ];

    const toggleApplicationImageSwiperClasses = (component, regions, add) => {
        component.classList.toggle(applicationImageSliderClass, add);

        regions.forEach(({ container, list, slide }) => {
            container.classList.toggle('swiper', add);
            container.querySelector(list).classList.toggle('swiper-wrapper', add);
            container.querySelectorAll(slide).forEach(el => el.classList.toggle('swiper-slide', add));
        });
    };

    const initApplicationImage = () => {
        document.querySelectorAll(applicationImageSelector).forEach(component => {
            const regions = applicationImageRegions(component);
            if (regions.some(({ container, list }) => container == null || container.querySelector(list) == null)) return;

            const [media, text] = regions;

            if (mq.matches) {
                if (instances.has(media.container)) return;

                // Open on whichever item the accordion is showing, so resizing
                // down doesn't leave the slide and the text panel disagreeing
                const openIndex = [...component.querySelectorAll('.accordion__item')]
                    .findIndex(item => item.classList.contains('accordion__item--active'));

                const shared = {
                    modules: [Controller, Navigation],
                    speed: 500,
                    slidesPerView: 1,
                    spaceBetween: 15,
                    loop: false,
                    initialSlide: Math.max(openIndex, 0),
                };

                toggleApplicationImageSwiperClasses(component, regions, true);

                const textSwiper = new Swiper(text.container, { ...shared });
                const mediaSwiper = new Swiper(media.container, {
                    ...shared,
                    navigation: {
                        prevEl: component.querySelector(applicationImagePrevSelector),
                        nextEl: component.querySelector(applicationImageNextSelector),
                    },
                    on: {
                        // Dragging either one drives the other; the accordion is told
                        // once, from here, so it can't be told twice per change
                        slideChange: (swiper) => activateApplicationImageItem(component, swiper.activeIndex, false),
                    },
                });

                mediaSwiper.controller.control = textSwiper;
                textSwiper.controller.control = mediaSwiper;

                instances.set(media.container, mediaSwiper);
                instances.set(text.container, textSwiper);
            } else if (instances.has(media.container)) {
                // Hand the slide the reader stopped on back to the cross-fade
                const { activeIndex } = instances.get(media.container);

                regions.forEach(({ container }) => {
                    // Cut the link first, or destroying one drives the other
                    const swiper = instances.get(container);
                    swiper.controller.control = undefined;
                    swiper.destroy(true, true);
                    instances.delete(container);
                });

                toggleApplicationImageSwiperClasses(component, regions, false);
                activateApplicationImageItem(component, activeIndex, true);
            }
        });
    };

    // Articles swiper — mobile only, the grid takes over above it
    const initArticles = () => {
        document.querySelectorAll(articlesSelector).forEach(container => {
            if (container.querySelector(articlesListSelector) == null) return;

            if (mq.matches) {
                if (instances.has(container)) return;

                toggleSwiperClasses(container, true, articlesListSelector, articlesItemSelector);
                instances.set(container, new Swiper(container, { ...mobileCarouselConfig }));
            } else if (instances.has(container)) {
                instances.get(container).destroy(true, true);
                instances.delete(container);
                toggleSwiperClasses(container, false, articlesListSelector, articlesItemSelector);
            }
        });
    };

    // Quote slider — scroll-locked
    const initQuotes = () => {
        document.querySelectorAll(quotesSelector).forEach(container => {
            if (instances.has(container)) return;

            const slideCount = container.querySelectorAll('.swiper-slide').length;

            const swiper = new Swiper(container, {
                speed: 500,
                slidesPerView: 1,
                spaceBetween: 15,
                loop: false,
            });

            instances.set(container, swiper);
            createScrollLockedSlides(container, swiper, slideCount);
        });
    };

    const escapeAttribute = (value) => value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/"/g, '&quot;');

    const syncBulletAria = (swiper) => {
        swiper.pagination?.bullets?.forEach(bullet => {
            if (bullet.classList.contains(bulletActiveClass)) bullet.setAttribute('aria-current', 'true');
            else bullet.removeAttribute('aria-current');
        });
    };

    const measureTallestSlide = (container) => {
        const slides = [...container.querySelectorAll('.swiper-slide')];
        const slideHeights = slides.map(slide => slide.style.height);

        container.style.height = '';
        container.classList.add(scrollSectionMeasuringClass);
        slides.forEach(slide => { slide.style.height = ''; });

        const tallest = slides.reduce((max, slide) => Math.max(max, slide.offsetHeight), 0);

        slides.forEach((slide, i) => { slide.style.height = slideHeights[i]; });
        container.classList.remove(scrollSectionMeasuringClass);

        return tallest;
    };

    const fitScrollSectionHeight = (container, swiper) => {
        const tallest = scrollSectionMq.matches ? measureTallestSlide(container) : 0;

        container.style.height = tallest > 0 ? `${tallest}px` : '';
        swiper.update();
    };

    // Scroll section — vertical, one panel per slide, scroll-locked
    const initScrollSections = () => {
        document.querySelectorAll(scrollSectionSelector).forEach(container => {
            if (instances.has(container)) return;

            const slideCount = container.querySelectorAll('.swiper-slide').length;
            const paginationEl = container.closest(scrollSectionInnerSelector)
                ?.querySelector(scrollSectionPaginationSelector);
            const bulletLabel = escapeAttribute(paginationEl?.dataset.bulletLabel ?? '');

            const swiper = new Swiper(container, {
                modules: [Pagination],
                speed: 500,
                direction: 'vertical',
                slidesPerView: 1,
                spaceBetween: 0,
                loop: false,
                // Unpinned this runs the same way the page does, so at the first
                // and last panel the drag has to go back to the page or there is
                // no way to scroll past the section. Needs loop off and the
                // default threshold of 0, which is what it has.
                touchReleaseOnEdges: true,
                ...(paginationEl && {
                    pagination: {
                        el: paginationEl,
                        clickable: true,
                        renderBullet: (index, className) =>
                            `<button type="button" class="${className}" aria-label="${bulletLabel.replace('{index}', index + 1)}"></button>`,
                    },
                }),
            });

            instances.set(container, swiper);

            swiper.on('paginationUpdate', () => syncBulletAria(swiper));
            syncBulletAria(swiper);

            fitScrollSectionHeight(container, swiper);
            document.fonts?.ready.then(() => fitScrollSectionHeight(container, swiper));

            let resizeTimer;
            window.addEventListener('resize', () => {
                clearTimeout(resizeTimer);
                resizeTimer = setTimeout(() => {
                    // Engaging the lock freezes the page and takes the scrollbar
                    // with it, which reads as a resize. Remeasuring then rewrites
                    // every slide's height mid-transition; the release brings the
                    // scrollbar back and this runs on that resize instead.
                    if (lockOwner) return;

                    fitScrollSectionHeight(container, swiper);
                }, 150);
            });

            createScrollLockedSlides(container, swiper, slideCount);
        });
    };

    const init = () => {
        initCards();
        initCounters();
        initContentBoxes();
        initArticles();
        initQuotes();
        initApplicationImage();
        initScrollSections();
        syncScrollLock();
    };

    mq.addEventListener('change', init);
    init();
}
